import store from "../app/store";
import { toast } from "react-toastify";

/**
 * Retrieves and opens a file from the server, using either a direct URL for HTML files
 * or a signed URL for other file types (e.g., PDFs, DOCX).
 *
 * - For HTML files, it opens the `key` URL directly in a new tab.
 * - For other file types, it fetches a signed URL from the backend and opens it.
 * - Displays toast messages on error and returns structured status.
 *
 * @async
 * @function GetUrlOfFiles
 * @param {string} key - The S3 key or URL of the file to access.
 * @param {"html" | "pdf" | "text" | string} type - The file type. `"html"` opens directly; others fetch signed URL.
 * @returns {Promise<{ data: string | null, error: string | null }>} A promise resolving to:
 * - `data`: The final URL opened (either direct or signed).
 * - `error`: `null` if successful; error message string otherwise.
 *
 * @example
 * const result = await GetUrlOfFiles("key -> aws s3 object key", "pdf");
 * if (!result.error) {
 *   console.log("Opened file at:", result.data);
 * } else {
 *   console.error("File open failed:", result.error);
 * }
 */

const GetUrlOfFiles = async (key, type) => {
  if (type === "html") {
    const a = document.createElement("a");
    a.href = key;
    a.rel = "noreferrer";
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return;
  }

  const serverDomain = process.env.REACT_APP_SERVER;
  const url = `${serverDomain}/other/getSignedUrl`;

  const token = store.getState().auth.token;

  if (!token) {
    toast.error("Unauthorized. Please log in again.");
    return { data: null, error: "No token found" };
  }

  const option = {
    method: "POST", // ✅ Fixed from PUT to POST
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ key }), // ✅ Raw key
  };

  try {
    const res = await fetch(url, option);
    if (!res.ok) {
      toast.error("Server Error, Try Again Later...");
      return { data: null, error: "Server error" };
    }

    const data = await res.json();

    if (!data?.data) {
      toast.error("Invalid file URL received.");
      return { data: null, error: "No signed URL returned" };
    }

    const a = document.createElement("a");
    a.href = data.data;
    a.rel = "noreferrer";
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    return { data: data.data, error: null };
  } catch (err) {
    console.error(err);
    toast.error("Something went wrong.");
    return { data: null, error: "Fetch failed" };
  }
};

export default GetUrlOfFiles;
